/**
 * useSavedProjects — the list of auto-saved projects (IndexedDB on the web,
 * the projects folder on desktop), newest first. Shared by the startup screen's
 * Recent projects list and the in-project "Recently saved" menu.
 */

import { useCallback, useState } from 'react';
import ipc from '../services/ipc';

/** Normalize `list-saved-projects` output into `{ name, filePath, lastSaved, ... }`, newest first. */
export function normalizeSavedProjects(list) {
  const normalized = (Array.isArray(list) ? list : []).map((p) => ({
    name: p?.name || p?.project_info?.name || '(unnamed)',
    filePath: p?.filePath || (p?.safeName ? `idb://projects/${p.safeName}` : null),
    lastSaved: p?.lastSaved || p?.project_info?.last_modified || null,
    chemistry: p?.chemistry && p.chemistry !== 'Unknown' ? p.chemistry : null,
    processCount: p?.processCount || 0,
  })).filter((p) => p.filePath);
  normalized.sort((a, b) => (b.lastSaved ? Date.parse(b.lastSaved) : 0) - (a.lastSaved ? Date.parse(a.lastSaved) : 0));
  return normalized;
}

export default function useSavedProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setProjects(normalizeSavedProjects(await ipc.invoke('list-saved-projects')));
    } catch (err) {
      console.error('[useSavedProjects] list-saved-projects failed:', err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Permanently removes the saved copy. Callers confirm with the user first.
  const remove = useCallback(async (entry) => {
    const result = await ipc.invoke('delete-saved-project', entry.filePath);
    if (!result?.success) throw new Error(result?.error || 'Delete failed');
    setProjects((prev) => prev.filter((p) => p.filePath !== entry.filePath));
  }, []);

  return { projects, loading, refresh, remove };
}
